import { useEffect, useState } from "react";
import { ClipboardList, LifeBuoy, MessageCircle, Search, ShieldCheck, Timer } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ComplaintSection from "../components/home/ComplaintSection";
import { usePageMeta } from "../components/service/ServiceBits";
import api from "../lib/api";

const STEPS_INFO = [
  { icon: ClipboardList, title: "Raise a complaint", desc: "Fill the form below — you get a unique ticket ID instantly." },
  { icon: Timer, title: "We respond", desc: "Our support desk reviews every complaint the same working day." },
  { icon: Search, title: "Track to resolution", desc: "Follow admin replies with your ticket ID and phone number." },
];

export default function Complaint() {
  const [contact, setContact] = useState(null);
  usePageMeta(
    "Raise a Complaint — Shipment Support | IndiaFast",
    "Raise a courier complaint and track it to resolution — delays, pickup issues, billing and delivery concerns handled by the IndiaFast support desk.",
    "/complaint"
  );
  useEffect(() => { api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {}); }, []);
  const waNum = (contact?.phone || "").replace(/\D/g, "").slice(-10);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="complaint-page">
      <Navbar />

      <section className="relative overflow-hidden bg-navy pb-16 pt-32 sm:pt-36" data-testid="complaint-hero">
        <div className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full bg-saffron/15 blur-3xl" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-saffron">
            <LifeBuoy className="h-4 w-4" /> Complaints &amp; Support
          </p>
          <h1 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Raise a Complaint. <span className="text-saffron">Track It to Resolution.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Delay, damage, billing or pickup — every complaint gets a unique ticket ID and replies from our team, visible on your ticket.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            {STEPS_INFO.map((s, i) => (
              <div key={i} data-testid={`complaint-step-${i}`}
                   className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-saffron/15 text-saffron"><s.icon className="h-4 w-4" /></span>
                <div>
                  <p className="text-xs font-bold text-white">{s.title}</p>
                  <p className="text-[11px] text-slate-400">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ComplaintSection />

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6" data-testid="complaint-next">
        <div className="flex flex-col items-center justify-between gap-4 rounded-2xl bg-navy p-6 sm:flex-row sm:p-8">
          <div>
            <p className="flex items-center gap-2 font-heading text-lg font-bold text-white"><ShieldCheck className="h-5 w-5 text-saffron" /> Already have a ticket?</p>
            <p className="mt-1 text-sm text-slate-300">Check replies and status anytime using your ticket ID and phone number.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/support" data-testid="complaint-track-ticket-cta"
                  className="rounded-full bg-saffron px-6 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-colors hover:bg-saffron-hover">
              Track My Ticket
            </Link>
            {waNum && (
              <a href={`https://wa.me/91${waNum}`} target="_blank" rel="noreferrer" data-testid="complaint-whatsapp-cta"
                 className="flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-600 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-emerald-500">
                <MessageCircle className="h-4 w-4" /> WhatsApp Us
              </a>
            )}
          </div>
        </div>
      </section>

      <Footer contact={contact} />
    </div>
  );
}
